import { useAuth } from 'utils/authcontextt';
import {
  useAgentUsersQuery,
  useCreateAgentMutation,
  useDeleteAgentMutation,
} from 'hooks/useAgents';
import type { AgentUserRow } from 'types/agents';
import { agentRealName } from 'types/agents';

export function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: users = [], isLoading } = useAgentUsersQuery(isAdmin);
  const create = useCreateAgentMutation();
  const remove = useDeleteAgentMutation();

  const toggle = async (row: AgentUserRow) => {
    if (row.isChatAgent) {
      await remove.mutateAsync(row.userId);
      return;
    }
    const alias = window.prompt(
      `Chattalias för ${agentRealName(row)} (namnet kunden ser i chatten):`,
      row.alias ?? '',
    );
    if (!alias || !alias.trim()) return;
    await create.mutateAsync({ userId: row.userId, alias: alias.trim() });
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-sm text-gray-600">Endast administratörer kan hantera användare.</div>
    );
  }

  return (
    <div className="h-[calc(100vh-96px)] overflow-y-auto bg-gray-50 p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-950">Användare</h1>
        <p className="mt-1 text-sm text-gray-500">
          Slå på "chattagent" för att låta någon svara i chatten – och ge dem ett alias.
        </p>
      </header>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {isLoading ? (
          <div className="p-4 text-sm text-gray-500">Laddar…</div>
        ) : users.length === 0 ? (
          <div className="p-4 text-sm text-gray-400">Inga användare.</div>
        ) : (
          users.map((row) => (
            <div
              key={row.userId}
              className="flex items-center justify-between gap-4 border-b px-5 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-950">
                  {agentRealName(row)}
                </div>
                <div className="truncate text-xs text-gray-500">{row.email}</div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-blue-600">
                  {row.isChatAgent && row.alias ? `alias: ${row.alias}` : '—'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.isChatAgent}
                  onClick={() => toggle(row)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    row.isChatAgent ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      row.isChatAgent ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default UsersPage;
